import {DI_TOKENS} from '@/di/di.tokens';
import {
  AnalyticsOnboardingUserType,
  AnalyticsStartOnboardingType,
} from '@/services/TrackingService/TrackingService.constants';
import {
  DAILY_GAME_ONBOARDING_SCRIPT,
  MAIN_ONBOARDING_SCRIPT,
  MainOnboardingFinishSteps,
  MainOnboardingStartSteps,
  MainOnboardingSteps,
  TooltipId,
} from '@/stores/OnboardingStore/OnboardingStore.constants';

import {injected} from 'brandi';

import {HTMLElementId, TOOLTIP_VISIBLE_TIME} from '@/constants';
import {ErrorsService} from '@/services/ErrorsService/ErrorsService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {NavigationClickerModalService} from '@/services/NavigationClickerModalService/NavigationClickerModalService';
import {IProjectApi} from '@/services/ProjectApi/types';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {GameStatusService} from '@/services/shared/GameStatusService/GameStatusService';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {GiftsStore} from '@/stores/GiftsStore/GiftsStore';
import {ImprovementsStore} from '@/stores/ImprovementsStore/ImprovementsStore';
import {LeaderBoardStore} from '@/stores/LeaderBoardStore/LeaderBoardStore';
import {OnboardingStore} from '@/stores/OnboardingStore/OnboardingStore';
import {UserStore} from '@/stores/UserStore/UserStore';
import {GlobalWindow} from '@/types';
import {DateUtils, HOURS_IN_DAY} from '@/utils/DateUtils';

import {ProjectDate} from '../../ProjectDate/ProjectDate';

interface OnboardingStatus {
  mainOnboardingStepList: string[];
  mainOnboardingIndex: number;
  dailyGameOnboardingStepList: string[];
  dailyGameOnboardingIndex: number;
  dailyGameTooltip: 0 | -1;
  openSettingsTooltip: 0 | -1;
  // 0 - показать
  // 1 - показали сегодня
  // -1 - сегодня не показывать
  soonRecalculationTooltip: 0 | 1 | -1;
  message: 0 | -1;
  timerForRecalculationTooltip: 0 | -1;
}

export class OnboardingService {
  private onboardingStatus: OnboardingStatus;
  private timeoutId: number;
  private isWasInitialized: boolean;

  constructor(
    private readonly projectApi: IProjectApi,
    private readonly onboardingStore: OnboardingStore,
    private readonly userStore: UserStore,
    private readonly giftsStore: GiftsStore,
    private readonly loggerService: LoggerService,
    private readonly navigationService: NavigationClickerModalService,
    private readonly trackingService: TrackingService,
    private readonly clickerStateStore: ClickerStateStore,
    private readonly leaderBoardStore: LeaderBoardStore,
    private readonly globalWindow: GlobalWindow,
    private readonly improvementsStore: ImprovementsStore,
    private readonly errorsService: ErrorsService,
    private readonly gameStatusService: GameStatusService,
    private readonly projectDate: ProjectDate,
  ) {
    this.timeoutId = -1;
    this.onboardingStatus = {
      mainOnboardingIndex: -1,
      mainOnboardingStepList: [],
      dailyGameOnboardingStepList: [],
      dailyGameOnboardingIndex: -1,
      dailyGameTooltip: -1,
      openSettingsTooltip: -1,
      soonRecalculationTooltip: -1,
      message: -1,
      timerForRecalculationTooltip: -1,
    };
    this.isWasInitialized = false;
  }

  public initOnboardingStatus(): void {
    if (this.isWasInitialized) return;

    this.isWasInitialized = true;

    this.onboardingStatus = this.parseOnboardingStatus(this.userStore.getOnboardingStatus());

    const firstImprovementUpgradeCost = this.improvementsStore.getState().improvements[0]?.upgradeCost;

    // откат к предыдущим шагам, если каким-то образом нет кристаллов, чтобы купить первое улучшение
    if (
      !this.isMainOnboardingCompleted() &&
      firstImprovementUpgradeCost &&
      this.clickerStateStore.getPoints() < firstImprovementUpgradeCost
    ) {
      const step = MAIN_ONBOARDING_SCRIPT[this.onboardingStatus.mainOnboardingIndex];

      if (step === MainOnboardingSteps.OpenImprovements) {
        this.onboardingStatus.mainOnboardingIndex -= 1;
      }

      if (step === MainOnboardingSteps.BuyImprovement) {
        this.onboardingStatus.mainOnboardingIndex -= 3;
      }

      if (step === MainOnboardingSteps.PauseForRenderImprovementsMap) {
        this.onboardingStatus.mainOnboardingIndex -= 2;
      }
    }

    // 5 - MainOnboardingSteps.PauseForRenderImprovementsMap
    // 9 - MainOnboardingSteps.TapsMining
    if (this.onboardingStatus.mainOnboardingIndex === 5 || this.onboardingStatus.mainOnboardingIndex === 9) {
      this.onboardingStatus.mainOnboardingIndex--;
    }

    // главный онбординг еще не закончен, ничего не показываем, чтобы не наложить тултипки
    if (!this.isMainOnboardingCompleted()) return;

    const nextRecalculationAt = this.leaderBoardStore.getNextRecalculationAt();

    if (nextRecalculationAt) {
      const isTodayRecalculation =
        DateUtils.millisecondsToHours(this.projectDate.getTimeBefore(nextRecalculationAt)) < HOURS_IN_DAY;

      // сегодня пересчет, но еще не показывали
      if (isTodayRecalculation && this.onboardingStatus.soonRecalculationTooltip === -1) {
        // нужно показать
        this.onboardingStatus.soonRecalculationTooltip = 0;

        if (this.isMainOnboardingCompleted()) {
          this.showSoonRecalculationTooltip();
        }
      }
      // уже показывали, но произошел пересчет, нужно сбросить, чтобы показать в следующий раз
      else if (!isTodayRecalculation && this.onboardingStatus.soonRecalculationTooltip === 1) {
        this.onboardingStatus.soonRecalculationTooltip = -1;
        this.saveOnboardingStatus();
      }
    }
  }

  public async claimGiftIfAvailable(): Promise<void> {
    try {
      if (this.userStore.isOnboardingGiftAvailable()) {
        const response = await this.projectApi.claimOnboardingGift();
        this.giftsStore.pushGifts(response.payload.gifts);
        this.giftsStore.setActiveTabUserGifts();
        this.giftsStore.setHasNewGifts(this.giftsStore.getHasNewGiftsByGiftList());
        this.userStore.setOnboardingGiftClaimed();
      }
    } catch (error) {
      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      }
    }
  }

  public showDailyGameOnboarding(): void {
    const {dailyGameOnboardingIndex, dailyGameOnboardingStepList} = this.onboardingStatus;

    if (dailyGameOnboardingIndex !== -1) {
      this.onboardingStore.setOnboardingStepList(
        dailyGameOnboardingStepList,
        dailyGameOnboardingStepList[dailyGameOnboardingIndex],
      );
    }
  }

  public showMainOnboarding(): void {
    const {mainOnboardingIndex, mainOnboardingStepList} = this.onboardingStatus;

    if (!this.isMainOnboardingCompleted()) {
      this.onboardingStore.setOnboardingStepList(mainOnboardingStepList, mainOnboardingStepList[mainOnboardingIndex]);
    }
  }

  public isDailyGameOnboardingActive(): boolean {
    const step = this.onboardingStore.getOnboardingStep();

    if (!step) return false;

    return this.onboardingStatus.dailyGameOnboardingStepList.includes(step);
  }

  public isMainOnboardingActive(): boolean {
    const step = this.onboardingStore.getOnboardingStep();

    if (!step) return false;

    return this.onboardingStatus.mainOnboardingStepList.includes(step);
  }

  public trackContinueOnboarding(): void {
    this.trackingService.trackStartOnboarding(
      this.userStore.isOnboardingGiftAvailable()
        ? AnalyticsOnboardingUserType.WithReward
        : AnalyticsOnboardingUserType.WithoutReward,
      AnalyticsStartOnboardingType.Continue,
    );
  }

  public trackSkipOnboarding(): void {
    this.trackingService.trackStartOnboarding(
      this.userStore.isOnboardingGiftAvailable()
        ? AnalyticsOnboardingUserType.WithReward
        : AnalyticsOnboardingUserType.WithoutReward,
      AnalyticsStartOnboardingType.Skip,
    );
  }

  public skipOnboarding(): void {
    this.onboardingStore.finishOnboarding();
    this.onboardingStatus = {
      ...this.onboardingStatus,
      // -2 это скип
      mainOnboardingIndex: -2,
      mainOnboardingStepList: [],
    };

    this.saveOnboardingStatus();
  }

  public hideOnboarding(): void {
    this.onboardingStore.finishOnboarding();
  }

  public isMainOnboardingCompleted(): boolean {
    return this.onboardingStatus.mainOnboardingIndex === -1 || this.onboardingStatus.mainOnboardingIndex === -2;
  }

  public isDailyGameOnboardingCompleted(): boolean {
    return this.onboardingStatus.dailyGameOnboardingIndex === -1;
  }

  public nextMainOnboardingStep = (count: number = 1): void => {
    if (this.isMainOnboardingCompleted()) return;
    let currentStepIndex: number = -1;

    for (let i = 0; i < count; i++) {
      currentStepIndex = this.onboardingStore.nextStep();
    }

    const currentStep = this.onboardingStore.getOnboardingStep();

    // трекаем завершение онбординга
    if (currentStep === MainOnboardingFinishSteps.FinishWithoutGift) {
      this.trackingService.trackFinishOnboarding(AnalyticsOnboardingUserType.WithoutReward);
    } else if (currentStep === MainOnboardingFinishSteps.FinishWithViewGift) {
      this.trackingService.trackFinishOnboarding(AnalyticsOnboardingUserType.WithReward);
    }

    // на этот костыльный шаг не сохраняем, иначе скрипт собьется при перезагрузке
    if (
      currentStep === MainOnboardingSteps.PauseForRenderImprovementsMap ||
      currentStep === MainOnboardingSteps.WaitBeforeTasksBackEnabled
    ) {
      return;
    }

    // здесь сброс на предыдущий PauseForRenderImprovementsMap, чтобы шаг с покупкой улучшения не скипнулся
    if (currentStep === MainOnboardingSteps.BuyImprovement || currentStep === MainOnboardingSteps.BackToMainFromTasks) {
      currentStepIndex--;
    }

    this.onboardingStatus.mainOnboardingIndex = currentStepIndex;

    this.saveOnboardingStatus();

    if (this.isMainOnboardingCompleted()) {
      this.showSoonRecalculationTooltip();
    }
  };

  public showOpenDailyGameTooltip(): void {
    if (this.onboardingStatus.dailyGameTooltip === -1) return;

    this.onboardingStore.setOnboardingStepList([TooltipId.OpenDailyGame], TooltipId.OpenDailyGame);

    this.timeoutId = this.globalWindow.setTimeout(() => {
      this.onboardingStore.finishOnboarding();
      this.globalWindow.document.removeEventListener('pointerdown', this.handlePreventClick);
    }, TOOLTIP_VISIBLE_TIME);

    this.globalWindow.document.addEventListener('pointerdown', this.handlePreventClick);

    this.onboardingStatus.dailyGameTooltip = -1;

    this.saveOnboardingStatus();
  }

  public showSoonRecalculationTooltip(): void {
    if (
      !this.isMainOnboardingCompleted() ||
      this.onboardingStatus.soonRecalculationTooltip === -1 ||
      this.onboardingStatus.soonRecalculationTooltip === 1
    ) {
      return;
    }

    this.onboardingStore.setOnboardingStepList([TooltipId.SoonRecalculation], TooltipId.SoonRecalculation);

    this.timeoutId = this.globalWindow.setTimeout(() => {
      this.onboardingStore.finishOnboarding();
    }, TOOLTIP_VISIBLE_TIME);

    this.globalWindow.document.addEventListener('pointerdown', this.handlePreventClick);

    this.onboardingStatus.soonRecalculationTooltip = 1;

    this.saveOnboardingStatus();
  }

  public forceFinishDailyGameTooltip(): void {
    if (this.onboardingStatus.dailyGameTooltip === -1) return;

    this.onboardingStatus.dailyGameTooltip = -1;

    this.saveOnboardingStatus();
  }

  public showSettingsTooltip(): void {
    if (this.onboardingStatus.openSettingsTooltip === -1) return;

    this.onboardingStore.setOnboardingStepList([TooltipId.OpenSettings], TooltipId.OpenSettings);

    this.timeoutId = this.globalWindow.setTimeout(() => {
      this.onboardingStore.finishOnboarding();
    }, TOOLTIP_VISIBLE_TIME);

    this.globalWindow.document.addEventListener('pointerdown', this.handlePreventClick);

    this.onboardingStatus.openSettingsTooltip = -1;

    this.saveOnboardingStatus();
  }

  public showTimerForRecalculationTooltip(): void {
    if (this.onboardingStatus.timerForRecalculationTooltip === -1) return;

    this.onboardingStore.setOnboardingStepList([TooltipId.TimerForRecalculation], TooltipId.TimerForRecalculation);

    this.timeoutId = this.globalWindow.setTimeout(() => {
      this.onboardingStore.finishOnboarding();
    }, TOOLTIP_VISIBLE_TIME);

    this.globalWindow.document.addEventListener('pointerdown', this.handlePreventClick);

    this.onboardingStatus.timerForRecalculationTooltip = -1;

    this.saveOnboardingStatus();
  }

  public forceFinishSettings(): void {
    if (this.onboardingStatus.openSettingsTooltip === -1) return;

    this.onboardingStatus.openSettingsTooltip = -1;

    this.saveOnboardingStatus();
  }

  public isOpenDailyGameTooltipViewed(): boolean {
    return this.onboardingStatus.dailyGameTooltip === -1;
  }

  public isSettingsTooltipViewed(): boolean {
    return this.onboardingStatus.openSettingsTooltip === -1;
  }

  public nextDailyGameOnboardingStep = (count: number = 1): void => {
    if (this.onboardingStatus.dailyGameOnboardingIndex === -1) return;

    let currentStepIndex: number = -1;

    for (let i = 0; i < count; i++) {
      currentStepIndex = this.onboardingStore.nextStep();
    }

    this.onboardingStatus.dailyGameOnboardingIndex = currentStepIndex;

    this.saveOnboardingStatus();
  };

  public attemptRestorePageWithOnboarding(): void {
    if (
      this.onboardingStore.isCurrentStep(MainOnboardingSteps.PauseForRenderImprovementsMap) ||
      this.onboardingStore.isCurrentStep(MainOnboardingSteps.MinePerHour) ||
      this.onboardingStore.isCurrentStep(MainOnboardingSteps.LeaderboardLocked)
    ) {
      this.navigationService.setCurrentModalTypeImprovements();
    }

    if (
      this.onboardingStore.isCurrentStep(MainOnboardingSteps.WaitBeforeTasksBackEnabled) ||
      this.onboardingStore.isCurrentStep(MainOnboardingSteps.BackToMainFromTasks)
    ) {
      this.navigationService.setCurrentModalTypeTasks();
    }
  }

  public showKotletkaMessage(): void {
    this.onboardingStore.setOnboardingStepList([TooltipId.KotletkaMessage], TooltipId.KotletkaMessage);

    this.timeoutId = this.globalWindow.setTimeout(() => {
      this.onboardingStore.finishOnboarding();
      this.globalWindow.document.removeEventListener('pointerdown', this.handlePreventClick);
    }, TOOLTIP_VISIBLE_TIME);

    this.onboardingStatus.message = -1;

    this.globalWindow.document.addEventListener('pointerdown', this.handlePreventClick);
  }

  private handlePreventClick = (e: PointerEvent): void => {
    const canvas = document.getElementById(HTMLElementId.GameCanvas);

    if (e.target !== canvas) {
      this.globalWindow.clearTimeout(this.timeoutId);
      this.onboardingStore.finishOnboarding();
      this.globalWindow.document.removeEventListener('pointerdown', this.handlePreventClick);
    }
  };

  private saveOnboardingStatus(): void {
    try {
      void this.projectApi.setOnboardingStatus(this.getOnboardingStringStatus());
    } catch (error) {
      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      }
    }
  }

  // статус онбординга выглядит как "number number number и тд" или пустая строка
  private parseOnboardingStatus(onboardingStatus: string): OnboardingStatus {
    // никакого статуса нет, значит нужно пройти онбординг
    if (!onboardingStatus) {
      return {
        mainOnboardingIndex: 0,
        mainOnboardingStepList: this.getMainOnboardingStepList(),
        dailyGameOnboardingStepList: DAILY_GAME_ONBOARDING_SCRIPT,
        dailyGameOnboardingIndex: 0,
        openSettingsTooltip: 0,
        dailyGameTooltip: 0,
        soonRecalculationTooltip: -1,
        message: -1,
        timerForRecalculationTooltip: 0,
      };
    }

    // если что-то есть, надо распарсить строку вида "number number number и тд"
    const indexList = onboardingStatus.split(' ');

    const parseIndex = (index: number): number => {
      if (Number.isNaN(index)) {
        this.loggerService.logHaveInvalidOnboardingIndexError(index);

        // 0 на случай если пришло что-то невалидное и онбординг не заблочил бы игру, а тултип был показан
        return 0;
      }

      return index;
    };

    return {
      mainOnboardingIndex: parseIndex(parseInt(indexList[0])),
      mainOnboardingStepList: this.getMainOnboardingStepList(),
      dailyGameOnboardingStepList: DAILY_GAME_ONBOARDING_SCRIPT,
      dailyGameOnboardingIndex: parseIndex(parseInt(indexList[1])),
      dailyGameTooltip: parseIndex(parseInt(indexList[2])) as 0 | -1,
      openSettingsTooltip: parseIndex(parseInt(indexList[3])) as 0 | -1,
      soonRecalculationTooltip: parseIndex(parseInt(indexList[4])) as 0 | -1,
      timerForRecalculationTooltip: parseIndex(parseInt(indexList[5])) as 0 | -1,
      message: -1,
    };
  }

  private getOnboardingStringStatus(): string {
    const {
      mainOnboardingIndex,
      dailyGameOnboardingIndex,
      dailyGameTooltip,
      openSettingsTooltip,
      soonRecalculationTooltip,
      timerForRecalculationTooltip,
    } = this.onboardingStatus;

    return `${mainOnboardingIndex} ${dailyGameOnboardingIndex} ${dailyGameTooltip} ${openSettingsTooltip} ${soonRecalculationTooltip} ${timerForRecalculationTooltip}`;
  }

  private getMainOnboardingStepList(): string[] {
    return MAIN_ONBOARDING_SCRIPT.reduce<string[]>((array, item) => {
      if (item === MainOnboardingSteps.Start) {
        if (this.userStore.isOnboardingGiftAvailable()) {
          array.push(MainOnboardingStartSteps.StartWithGift);
        } else {
          array.push(MainOnboardingStartSteps.StartCommon);
        }
      } else if (item === MainOnboardingSteps.Finish) {
        if (this.userStore.isOnboardingGiftAvailable()) {
          array.push(MainOnboardingFinishSteps.FinishWithViewGift);
        } else {
          array.push(MainOnboardingFinishSteps.FinishWithoutGift);
        }
      } else {
        array.push(item);
      }

      return array;
    }, []);
  }
}

injected(
  OnboardingService,
  DI_TOKENS.projectApi,
  DI_TOKENS.onboardingStore,
  DI_TOKENS.userStore,
  DI_TOKENS.giftsStore,
  DI_TOKENS.loggerService,
  DI_TOKENS.navigationClickerModalService,
  DI_TOKENS.trackingService,
  DI_TOKENS.clickerStateStore,
  DI_TOKENS.leaderBoardStore,
  DI_TOKENS.globalWindow,
  DI_TOKENS.improvementsStore,
  DI_TOKENS.errorsService,
  DI_TOKENS.gameStatusService,
  DI_TOKENS.projectDate,
);
